import type {AnyAsyncFunction, AnyFunction, Promised} from './types'

export interface SuccessfulAttempt<T> {
    value: T
    didThrow: false
    error: null
}

export interface FailedAttempt<E> {
    error: E
    didThrow: true
    value: null
}

export type Attempt<T, E> = SuccessfulAttempt<T> | FailedAttempt<E>

/** Create a successful `Attempt` object */
export const successfulAttempt = <T>(value: T) => ({value, didThrow: false, error: null} as SuccessfulAttempt<T>)

/** Create a failed `Attempt` object */
export const failedAttempt = <E>(error: E) => ({error, didThrow: true, value: null} as FailedAttempt<E>)

/** Run the function `f` with the provided `args` and wrap the result in an `Attempt` instead of throwing errors */
export function attempt<F extends AnyFunction, E = unknown>(f: F, ...args: Parameters<F>): Attempt<ReturnType<F>, E> {
    try {
        return {value: f(...args), didThrow: false, error: null}
    } catch (e) {
        return {error: e as E, didThrow: true, value: null}
    }
}

/** Run the function `f` with the provided `args` and return `defaultValue` instead of throwing errors */
export function defaultAttempt<F extends AnyFunction>(
    defaultValue: ReturnType<F>,
    f: F,
    ...args: Parameters<F>
): ReturnType<F> {
    try {
        return f(...args)
    } catch (e) {
        return defaultValue
    }
}

/** Run the async function `f` with the provided `args` and wrap the result in an `Attempt` instead of throwing errors */
export async function asyncAttempt<F extends AnyAsyncFunction, E = unknown>(
    f: F,
    ...args: Parameters<F>
): Promise<Attempt<Promised<F>, E>> {
    try {
        return {value: await f(...args), didThrow: false, error: null}
    } catch (e) {
        return {error: e as E, didThrow: true, value: null}
    }
}

/** Run the async function `f` with the provided `args` and return `defaultValue` instead of throwing errors */
export async function asyncDefaultAttempt<F extends AnyAsyncFunction>(
    defaultValue: Promised<F>,
    f: F,
    ...args: Parameters<F>
): Promise<Promised<F>> {
    try {
        return await f(...args)
    } catch (e) {
        return defaultValue
    }
}

/** takes an async function F, and returns a function which ensures all calls to it will be synchronized and not run concurrently */
export function synchronized<F extends AnyAsyncFunction>(fn: F): F {
    let lock = Promise.resolve()

    return (async (...args: Parameters<F>): Promise<ReturnType<F>> => {
        lock = lock.then(() => fn(...args))
        return lock as ReturnType<F>
    }) as F
}
